import React, { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';

import {
  Character,
  RollPlus,
  CharacterRoll,
  CharacterSheetInterface,
  RolledValue
} from "@dndModels";
import { AbilityScoreType } from "@dndModels/Types";
import { ABILITY_SCORE_ABBREV_TO_FULL_MAP } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import ButtonBox from "@components/input/buttons/ButtonBox";
import InfoButton from "@components/input/buttons/InfoButton";

interface Props {
  ability: AbilityScoreType;
  character: Character;
  char_interface: CharacterSheetInterface;
  horizontal: boolean;
  shared: boolean;
  onRoll: (ability: AbilityScoreType, roll: RolledValue[], anchorEl: HTMLSpanElement) => void;
  onOpenDrawer: (ability: AbilityScoreType) => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterAbilityScore({
  ability,
  character,
  char_interface,
  horizontal,
  shared,
  onRoll,
  onOpenDrawer,
}: Props) {
  const [modifier, setModifier] = useState<number | null>(null);
  const [score, setScore] = useState<number | null>(null);

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const recalculated = useCallback(() => {
    // console.log(`Ability Score ${ability} - Recalculated`);
    // console.log(character.current_ability_scores.getAbilityScore(ability));
    // console.log(char_interface.current_ability_scores.getAbilityScore(ability));
    setTimeout(() => {
      setModifier(char_interface.current_ability_scores.getModifier(ability));
      setScore(char_interface.current_ability_scores.getAbilityScore(ability));
    }, 100);
  }, [ability]);

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [recalculated]);
  
  const enabled = api.user && (
    character.owner_id === api.user._id ||
    (character.campaign !== null && character.campaign.owner_id === api.user._id)
  );

  if (modifier === null || score === null) return null;

  const adjustedModifier = char_interface.jack_of_all_trades
    ? modifier + Math.floor(character.proficiency_modifier * 0.5)
    : modifier;

  const { advantage, greater_advantage, advantages } = char_util.getAdvantage(character, "Ability Check", ability);

  const handleRollClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    const abbrev = common_util.ability_score_abbreviation(ability);
    const char_roll = (char_interface.ability_checks as any)[abbrev] as CharacterRoll;
    const roll_plus = new RollPlus();
    roll_plus.flat = char_roll.modifier;
    const rolls: RollPlus[] = [roll_plus, ...char_roll.rolls];
    const rolled = char_util.rollD20({ char_interface: character, rolls, type: "Ability Check", use_ability_score: ability, advantage, greater_advantage });
    onRoll(ability, rolled, event.currentTarget);
  };

  const renderAdvantages = () => (
    <Grid container spacing={0} direction="row" style={{ maxHeight: window.innerHeight - 34 }}>
      <Grid
        className="header centered"
        size={{
          xs: 12
        }}>
        Advantage Source(s)
      </Grid>
      {advantages.map(a => (
        <Grid
          key={a.true_id}
          size={{
            xs: 12
          }}>
          {a.advantage.disadvantage ? "Disadvantage" : "Advantage"} from {a.cfb_name} on {a.source_name}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Grid
      className="centered"
      size={{
        xs: horizontal ? 2 : 4
      }}>
      <div style={{ width: "95px" }}>
        <Grid container spacing={0} direction="row" className="curved_border">
          <Grid
            size={{
              xs: 12
            }}>
            <div
              className="centered"
              style={{ fontSize: "11px", fontWeight: "bold", cursor: enabled ? "pointer" : "inherit" }}
              onClick={() => { if (enabled) onOpenDrawer(ability); }}
            >
              {ABILITY_SCORE_ABBREV_TO_FULL_MAP[ability]}
            </div>
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              style={{ fontSize: "24px", fontWeight: "bold", lineHeight: "1.1" }}
              name={common_util.addPlusMaybe(adjustedModifier)}
              disabled={!enabled && !shared}
              onClick={handleRollClick}
            />
          </Grid>
          {advantage !== 0 && (
            <Grid
              size={{
                xs: 12
              }}>
              {advantage > 0 && greater_advantage ? (
                <span style={{ color: "green", fontWeight: "bold" }}>GR. ADV</span>
              ) : advantage > 0 ? (
                <span style={{ color: "green" }}>ADV</span>
              ) : (
                <span style={{ color: "red" }}>DISADV</span>
              )}
              <InfoButton>{renderAdvantages()}</InfoButton>
            </Grid>
          )}
          <Grid
            className="centered"
            size={{
              xs: 12
            }}>
            <div
              className="curved_border centered"
              style={{ width: "35px", fontSize: "11px", fontWeight: "bold", cursor: enabled ? "pointer" : "inherit" }}
              onClick={() => { if (enabled) onOpenDrawer(ability); }}
            >
              {score}
            </div>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
}